import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {
  Category,
  Question,
  QuestionGroup,
  QuestionTemplate
} from "@core/model/retailer/application-form.model";
import {PageOffering} from "@core/model/side-nav.model";
import {Router} from "@angular/router";
import {ConsoleContextService} from "@security/console-context.service";
import {lastValueFrom} from "rxjs";
import {ControlType, QuestionItem} from "../../../../application-form-builder/add-questions/question-components.model";
import {Survey} from "../../../surveys.model";
import {SurveyFormBuilderService} from "../survey-form-builder.service";
import {SurveyService} from "../../../survey.service";

@Component({
  selector: 'app-survey-form-builder-configure-smart-form',
  templateUrl: './survey-form-builder-configure-smart-form.component.html',
  styleUrl: './survey-form-builder-configure-smart-form.component.scss'
})
export class SurveyFormBuilderConfigureSmartFormComponent implements OnInit {
  readonly ControlType = ControlType;

  @Input() formTemplate!: Survey;
  @Input() offering!: PageOffering;
  @Output() formChange = new EventEmitter<Survey>();

  @Input() pendingChanges: boolean = false;
  @Output() pendingChangesChange = new EventEmitter<boolean>();

  questionItems: Question[] = [];
  selectedItem: Question | null = null;
  controlTypesFilterList = [
    ControlType.FILE,
    ControlType.TEXT,
    ControlType.LOCATION,
    ControlType.DATE,
    ControlType.HEADING,
    ControlType.HEADING_TEXT,
    ControlType.INFO_FILE
  ];
  selectedQuestionGroup: QuestionGroup | null = null;


  constructor(private formBuilderService: SurveyFormBuilderService,
              private surveyService: SurveyService,
              private router: Router,
              private context: ConsoleContextService) {
  }

  ngOnInit(): void {
    this.formTemplate.payload.forEach((category: Category) => {
      category.questionGroups.forEach(questionGroup => {
        if (questionGroup.questions.length == 1) {
          if (questionGroup.importanceRating == null && this.canBeScored(questionGroup.questions[0])) {
            questionGroup.importanceRating = 0;
          }
          if (!this.canBeScored(questionGroup.questions[0])) questionGroup.importanceRating = null;
          this.questionItems.push(...questionGroup.questions);
        }
      });
    });
  }

  markPendingChanges(value: boolean): void {
    this.pendingChanges = value;
    this.pendingChangesChange.emit(value);
  }

  public saveForm() {
    const updateRequest = this.formBuilderService.mapToUpdateSurveyRequest(this.formTemplate);

    this.formBuilderService.updateFormTemplate(updateRequest)
      .subscribe({
        next: value => {
          this.formTemplate = value;
          this.markPendingChanges(false);
          this.formChange.emit(this.formTemplate);
        },
        error: err => console.log(err)
      });
    // this.formChange.emit(this.formTemplate);
  }

  //todo - move to form builder service in both survey smart form and survey questions components
  // public previewForm() {
  //   const locationOffering = this.context.getCurrentLocation()?.locationOfferings?.find((offering: any) => offering.offeringUuid === this.offering?.offeringUuid)!;
  //   const url = this.router.serializeUrl(this.router.createUrlTree([`/preview-apply/${this.formTemplate.code}`],
  //     {
  //       queryParams: {
  //         locationCode: locationOffering.locationCode!,
  //         offeringCode: locationOffering.offeringCode!,
  //         embedKey: locationOffering.embedKey!
  //       }
  //     }));
  //   window.open(url, '_blank');
  // }

  selectQuestionItem(question: Question): void {
    if (this.canBeScored(question) && !question.disableScoring) {
      this.selectedItem = question;
      this.selectedQuestionGroup = this.getQuestionGroup(question.questionGroupUuid);
    }
  }

  getQuestionGroup(groupUuid: string): QuestionGroup | null {
    let questionGroups: QuestionGroup[] = this.formTemplate.payload
      .flatMap(category => category.questionGroups)
      .filter(groups => groups.uuid === groupUuid);
    if (questionGroups.length > 0) {
      return questionGroups[0];
    }
    return null;
  }

  canBeScored(question: Question): Boolean {
    return !this.controlTypesFilterList.includes(question.questionTemplate.controlType);
  }

  displayCantBeScoredMessage(question: Question): string {
    if (!this.canBeScored(question)) {
      const template = this.formBuilderService.getControlTemplate(question.questionTemplate);
      return `${template?.label} questions can't be scored`;
    }
    if (question.disableScoring) {
      return 'Scoring for this question has been disabled';
    }
    return '';
  }

  updateQuestionGroup($event: QuestionGroup): void {
    this.formTemplate.payload
      .flatMap(category => category.questionGroups)
      .filter(groups => groups.uuid === $event.uuid)
      .map(() => $event);
    this.markPendingChanges(true);
  }

  getQuestionTemplate(question: Question): QuestionTemplate {
    return question.questionTemplate;
  }

  toQuestionItem(question: Question): QuestionItem {
    return {
      uuid: question.questionGroupUuid,
      template: this.formBuilderService.getControlTemplate(question.questionTemplate)!,
      questionConfig: this.getQuestionGroup(question.questionGroupUuid),
      temp: false
    };
  }

  updateGroupRating(value: number): void {
    if (this.selectedQuestionGroup) {
      this.selectedQuestionGroup!.importanceRating = value;
      this.markPendingChanges(true);
    }
  }
}
